/**
 * useAIChat Hook
 * Manages AI Chat state: messages, sending (streaming with a buffered fallback),
 * Stop, conversation history, and feedback.
 *
 * Streaming contract (docs/AI_Chat_Phase1_Scope.md section 2.1 step 9):
 *   - every send carries a client-generated clientRequestId;
 *   - the streaming function URL is tried first; if the build has no URL, or the
 *     request fails before the first event, ONE legacy POST /ai-chat with the same
 *     id renders the buffered answer;
 *   - a 409 carrying a conversationId means the question was already accepted:
 *     the conversation is reloaded through the history form to show the answer;
 *   - text deltas are scoped by round: text from a round that then ran tools is
 *     interim and is replaced when the next round's text arrives.
 */

import { useState, useCallback, useRef } from 'react';
import {
  sendAIChatMessage,
  streamAIChatMessage,
  listAIChatConversations,
  loadAIChatHistory,
  submitAIChatFeedback,
  ConversationSummary,
  StreamEvent,
} from '../../services/aiChatApi';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  toolsUsed?: string[];
  isError?: boolean;
  conversationId?: string;
  messageIndex?: number;
  rating?: 'thumbs_up' | 'thumbs_down' | null;
  /** True while the answer is still arriving. */
  streaming?: boolean;
  /** Running status line ("Checked technician performance for 4 locations"). */
  status?: string;
  /** The stream ended without the answer; offer "check again" (reloads the conversation). */
  needsRecheck?: boolean;
}

export const INCOMPLETE_ANSWER_TEXT = 'The connection dropped before the answer finished. It is being saved and will be here when you check again.';
export const STOPPED_ANSWER_TEXT = 'Stopped. The full answer is still being saved; check again to see it.';
export const PENDING_ANSWER_TEXT = 'This question was received and is still being answered. Check again in a moment to see the answer.';
export const BUSY_ERROR_TEXT = 'Still answering your last question. Please wait for it to finish, then send again.';

/** The 409 body's `error` when the clientRequestId was already accepted (vs. "Conversation busy"). */
const DUPLICATE_REQUEST_CODE = 'Duplicate request';

function newRequestId(): string {
  const c = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useAIChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const messageIdCounter = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);

  const nextId = () => {
    messageIdCounter.current += 1;
    return `msg-${messageIdCounter.current}`;
  };

  const updateMessage = (id: string, patch: Partial<ChatMessage> | ((m: ChatMessage) => Partial<ChatMessage>)) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...(typeof patch === 'function' ? patch(m) : patch) } : m)));
  };

  const removeMessages = (ids: string[]) => {
    setMessages((prev) => prev.filter((m) => !ids.includes(m.id)));
  };

  /**
   * Fetch the newest page of a conversation and render it (empty on a backend without
   * the history form). Returns the rendered rows, or null when the page was unavailable.
   */
  const loadConversation = useCallback(async (id: string): Promise<ChatMessage[] | null> => {
    setConversationId(id);
    setError(null);
    const result = await loadAIChatHistory(id);
    if (!result.success) {
      setMessages([]);
      setError(result.error || 'Could not load the conversation.');
      return null;
    }
    if (!result.data) {
      setMessages([]);
      return null;
    }
    const rows: ChatMessage[] = result.data.messages.map((row) => ({
      id: `hist-${row.messageIndex}`,
      role: row.role,
      content: row.content,
      timestamp: row.timestamp,
      toolsUsed: row.toolsUsed,
      conversationId: id,
      messageIndex: row.messageIndex,
      rating: row.role === 'assistant' ? null : undefined,
    }));
    setMessages(rows);
    return rows;
  }, []);

  /**
   * Send a question. Resolves true when the question was accepted (the input may clear),
   * false when it was not sent (the input keeps its text).
   */
  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    const trimmed = content.trim();
    if (!trimmed || isLoadingRef.current) return false;

    setError(null);
    isLoadingRef.current = true;
    setIsLoading(true);

    const clientRequestId = newRequestId();
    const userMsg: ChatMessage = { id: nextId(), role: 'user', content: trimmed, timestamp: new Date().toISOString() };
    const assistantId = nextId();
    const placeholder: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      streaming: true,
      status: 'Thinking…',
      rating: null,
    };
    setMessages((prev) => [...prev, userMsg, placeholder]);

    const controller = new AbortController();
    abortRef.current = controller;
    let deltaRound: number | null = null;
    // Held in an object: TypeScript cannot see assignments made inside the onEvent closure.
    const started: { ids: { conversationId: string; messageIndex: number } | null } = { ids: null };

    const onEvent = (event: StreamEvent) => {
      switch (event.type) {
        case 'start':
          started.ids = { conversationId: event.conversationId, messageIndex: event.messageIndex };
          setConversationId(event.conversationId);
          updateMessage(assistantId, { conversationId: event.conversationId });
          break;
        case 'progress':
          updateMessage(assistantId, { status: event.headline });
          break;
        case 'delta': {
          const round = event.round;
          const replace = deltaRound !== null && deltaRound !== round;
          deltaRound = round;
          updateMessage(assistantId, (m) => ({ content: (replace ? '' : m.content) + event.text, status: undefined }));
          break;
        }
        default:
          break;
      }
    };

    const finish = () => {
      abortRef.current = null;
      isLoadingRef.current = false;
      setIsLoading(false);
    };

    const showError = (text: string, removeUserBubble: boolean): false => {
      if (removeUserBubble) {
        removeMessages([userMsg.id, assistantId]);
      } else {
        updateMessage(assistantId, { content: text, isError: true, streaming: false, status: undefined });
      }
      finish();
      return false;
    };

    const finalize = (ids: { conversationId: string; messageIndex: number }, patch: Partial<ChatMessage> = {}) => {
      setConversationId(ids.conversationId);
      updateMessage(assistantId, { streaming: false, status: undefined, conversationId: ids.conversationId, messageIndex: ids.messageIndex, rating: null, ...patch });
      finish();
      return true;
    };

    /**
     * The question was already accepted (409 "Duplicate request"): show the saved
     * conversation instead of running it twice. The original invocation is usually still
     * running, so when the answer row is not there yet, leave a check-again bubble.
     */
    const recoverAccepted = async (acceptedConversationId: string, acceptedMessageIndex?: number) => {
      const rows = await loadConversation(acceptedConversationId);
      // Without the accepted index (the 409 can arrive before beginTurn recorded it) the answer
      // cannot be told apart from an older one on an existing conversation: treat it as pending.
      const answered = rows !== null && acceptedMessageIndex !== undefined
        && rows.some((m) => m.role === 'assistant' && m.messageIndex === acceptedMessageIndex);
      if (!answered) {
        const pending: ChatMessage = {
          id: nextId(),
          role: 'assistant',
          content: PENDING_ANSWER_TEXT,
          timestamp: new Date().toISOString(),
          needsRecheck: true,
          conversationId: acceptedConversationId,
        };
        setMessages((prev) => [...prev, pending]);
      }
      finish();
      return true;
    };

    /** Another turn holds the conversation lock (409 "Conversation busy"): nothing was sent. */
    const rejectBusy = () => {
      setError(BUSY_ERROR_TEXT);
      return showError(BUSY_ERROR_TEXT, true);
    };

    const legacyFallback = async (): Promise<boolean> => {
      updateMessage(assistantId, { status: 'Working on it…' });
      const result = await sendAIChatMessage(trimmed, conversationId || undefined, clientRequestId);
      if (result.success && result.data) {
        updateMessage(assistantId, { content: result.data.response, toolsUsed: result.data.toolsUsed });
        return finalize({ conversationId: result.data.conversationId, messageIndex: result.data.messageIndex });
      }
      if (result.status === 409) {
        if (result.errorCode === DUPLICATE_REQUEST_CODE && result.conversationId) return recoverAccepted(result.conversationId, result.messageIndex);
        return rejectBusy();
      }
      if (result.retryAfterSeconds) setError(`Rate limit reached. Please wait ${Math.ceil(result.retryAfterSeconds / 60)} minutes.`);
      else setError(result.error || 'Something went wrong. Please try again.');
      return showError(result.error || 'Something went wrong. Please try again.', true);
    };

    try {
      const outcome = await streamAIChatMessage({
        message: trimmed,
        conversationId: conversationId || undefined,
        clientRequestId,
        signal: controller.signal,
        onEvent,
      });

      switch (outcome.kind) {
        case 'done':
          return finalize(outcome, { toolsUsed: outcome.metrics?.toolsUsed?.length ? outcome.metrics.toolsUsed : undefined });
        case 'error':
          updateMessage(assistantId, { content: outcome.message, isError: true, streaming: false, status: undefined });
          finish();
          return true;
        case 'aborted':
          updateMessage(assistantId, (m) => ({ streaming: false, status: undefined, needsRecheck: true, content: m.content ? `${m.content}\n\n${STOPPED_ANSWER_TEXT}` : STOPPED_ANSWER_TEXT, conversationId: started.ids?.conversationId ?? m.conversationId }));
          finish();
          return true;
        case 'incomplete':
          updateMessage(assistantId, { streaming: false, status: undefined, needsRecheck: true, content: INCOMPLETE_ANSWER_TEXT, conversationId: started.ids?.conversationId });
          finish();
          return true;
        case 'rejected':
          if (outcome.status === 409) {
            if (outcome.error === DUPLICATE_REQUEST_CODE && outcome.conversationId) return recoverAccepted(outcome.conversationId, outcome.messageIndex);
            return rejectBusy();
          }
          if (outcome.status === 429) {
            setError(`Rate limit reached. Please wait ${Math.ceil((outcome.retryAfterSeconds || 3600) / 60)} minutes.`);
            return showError(outcome.message || 'Rate limit exceeded.', true);
          }
          setError(outcome.message || outcome.error || 'The request was rejected.');
          return showError(outcome.message || outcome.error || 'The request was rejected.', true);
        case 'unavailable':
        case 'failed-before-events':
          return legacyFallback();
        default:
          return showError('An unexpected error occurred. Please try again.', true);
      }
    } catch (err) {
      return showError('An unexpected error occurred. Please try again.', true);
    }
  }, [conversationId, loadConversation]);

  /** Abort the open stream. The Lambda finishes and saves the answer regardless. */
  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /** "Check again" on a bubble whose stream was cut: reload the saved conversation. */
  const checkAgain = useCallback(async (messageId: string) => {
    const message = messages.find((m) => m.id === messageId);
    const id = message?.conversationId || conversationId;
    if (!id) return;
    await loadConversation(id);
  }, [messages, conversationId, loadConversation]);

  const startNewConversation = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationId(null);
    setError(null);
  }, []);

  const loadConversationList = useCallback(async () => {
    const result = await listAIChatConversations();
    if (result.success && result.data) {
      setConversations(result.data);
    }
  }, []);

  const rateMessage = useCallback(async (messageId: string, rating: 'thumbs_up' | 'thumbs_down') => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !message.conversationId || message.messageIndex === undefined) return;

    // Same thumb click = no-op
    if (message.rating === rating) return;

    const previousRating = message.rating;

    // Optimistic update
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, rating } : m
    ));

    const result = await submitAIChatFeedback(message.conversationId, message.messageIndex, rating);

    if (!result.success) {
      // Revert on failure
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, rating: previousRating } : m
      ));
    }
  }, [messages]);

  return {
    messages,
    conversationId,
    isLoading,
    error,
    conversations,
    sendMessage,
    stopStreaming,
    checkAgain,
    rateMessage,
    startNewConversation,
    loadConversation,
    loadConversationList,
  };
}
